import dynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import EllipsisText from "react-ellipsis-text";
import { IoIosSearch } from "react-icons/io";
import { RxHamburgerMenu } from "react-icons/rx";
import { useScrollDirection } from "react-use-scroll-direction";

import { useStoreContext } from "../../contexts/StoreDataContext";
import { toCdnUrl } from "../../lib/cdn";
import { resolveImage } from "../../lib/media";
import useStore from "../../store/search-store";
import Picture from "../Picture";
import { parseFileName } from "../utils/fileutil";
import CartIconSvg from "./CartIconSvg";

const SearchSidebar = dynamic(() => import("./SearchSidebar"), {
  loading: () => null,
  ssr: false,
});

const NavBar = (props) => {
  const context = useStoreContext();
  const isProplanActive = context.isProplanActive;

  const [direction, setDirection] = useState("");
  const { isScrollingUp, isScrollingDown } = useScrollDirection();
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const { isSearchOpen, setIsSearchOpen, shouldSearchReopen, setShouldSearchReopen } = useStore(
    (state) => ({
      isSearchOpen: state.isSearchOpen,
      setIsSearchOpen: state.setIsSearchOpen,
      shouldSearchReopen: state.shouldSearchReopen,
      setShouldSearchReopen: state.setShouldSearchReopen,
    }),
  );
  const searchInputRef = useRef(null);
  const router = useRouter();
  const { homePage = false, showSearch = true } = props;

  useEffect(() => {
    if (shouldSearchReopen === router.asPath && !isSearchOpen) {
      setIsSearchOpen(true);
    } else {
      setIsSearchOpen(false);
    }
  }, [router.asPath, setIsSearchOpen, setShouldSearchReopen, shouldSearchReopen]);

  useEffect(() => {
    isScrollingDown && setDirection("down");
    isScrollingUp && setDirection("up");
  }, [isScrollingDown, isScrollingUp]);

  const checkoutShouldHide = () => {
    if (typeof window !== "undefined" && window.scrollY > 120) {
      return true;
    }
    return false;
  };

  const makeBGWhite = () => {
    let shouldHide = false;
    if (typeof window != "undefined") {
      if (window.scrollY > 30) {
        shouldHide = true;
      }
    }
    return shouldHide;
  };

  const hasBanner = props.store?.storeBanner?.text ?? false;
  const isShopPage = router.pathname === "/shop";

  return (
    <>
      <header
        className={
          homePage
            ? `z-50 flex h-[4.5rem] w-full items-center justify-between bg-background transition-all duration-300 ${
                hasBanner ? (isShopPage ? "sticky top-0" : "top-20") : "top-0"
              } ${direction === "up" ? "opacity-100" : ""} ${
                direction === "down" && checkoutShouldHide() ? "-top-16 opacity-0" : ""
              }`
            : `fixed left-1/2 top-0 z-[99] flex h-[72px] w-full max-w-[var(--max-width)] -translate-x-1/2 flex-row items-center justify-between bg-background opacity-100 transition-all duration-300 ease-in-out ${direction === "up" ? "bg-background" : ""} ${direction === "down" && checkoutShouldHide() ? "-top-[60px]" : ""} ${direction === "down" && makeBGWhite() ? "bg-background" : ""} mb-5`
        }
      >
        <div className="mx-auto flex h-[5rem] w-full max-w-[var(--max-width)] items-center justify-between px-6 py-2">
          <div className="relative z-10 flex w-full items-center justify-center">
            {isProplanActive && (
              <button
                className="absolute left-0 cursor-pointer text-primary"
                aria-label="Toggle side navigation menu"
                onClick={() => props.toggleSideNav()}
              >
                <RxHamburgerMenu size={28} />
              </button>
            )}

            {isProplanActive &&
            props.store?.logo &&
            resolveImage(props.store.logo, props.store.logoV2, "logo").isV2 ? (
              <Picture
                resolved={resolveImage(props.store.logo, props.store.logoV2, "logo")}
                alt={`${props.store.storeName} Logo`}
                width={props.theme?.logoShape === "rectangle" ? 120 : 60}
                height={60}
                loading="eager"
                imgClassName={`object-contain ${props.theme?.logoShape === "rectangle" ? "" : "rounded-full"}`}
                onClick={() => router.push("/")}
              />
            ) : isProplanActive && props.store?.logo ? (
              <Image
                unoptimized={true}
                src={(() => {
                  const logo = props.store.logo;
                  if (logo.includes("dm2buy")) {
                    // eslint-disable-next-line no-undef
                    const urlPath = logo ? new URL(logo).pathname : "";
                    const extractedImageName = parseFileName(urlPath).name;
                    return `https://dm2buy-resize-dynamic-cebdcaefgydgh6hu.z02.azurefd.net/dm2buy/${extractedImageName}.jpg?quality=50`;
                  }
                  return toCdnUrl(logo);
                })()}
                alt={`${props.store.storeName} Logo`}
                width={props.theme?.logoShape === "rectangle" ? 120 : 60}
                height={60}
                className={`object-contain ${props.theme?.logoShape === "rectangle" ? "" : "rounded-full"}`}
                layout="fixed"
                onClick={() => router.push("/")}
              />
            ) : (
              <h2
                className="inline-block cursor-pointer text-center text-lg font-bold text-primary"
                onClick={() => (window.location.href = window.location.origin)}
              >
                <EllipsisText text={props.storeName} length={25} tail="..." />
              </h2>
            )}

            <div className="absolute right-0 z-10 flex gap-10">
              {isProplanActive && showSearch && (
                <button
                  className="cursor-pointer text-primary"
                  aria-label="Open search menu"
                  onClick={() => {
                    setIsSearchOpen(!isSearchOpen);
                    if (searchInputRef.current && !isSearchOpen) searchInputRef.current.focus();
                  }}
                >
                  <IoIosSearch size={26} />
                </button>
              )}

              {props.cartActive && !props.hideInAdvance && (
                <span className="mt-0.5">
                  <button
                    className="mt-1.5 cursor-pointer text-primary"
                    aria-label="Open cart"
                    onClick={() => props.handleShowCart(true)}
                  >
                    <CartIconSvg stroke={0.5} />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <SearchSidebar
        isOpen={isSearchOpen}
        toggleDrawer={() => setIsSearchOpen(!isSearchOpen)}
        store={props.store}
        searchRef={searchInputRef}
      />
    </>
  );
};

export default NavBar;
